import { createApp, markRaw, type DirectiveBinding } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import rg4js from 'raygun4js'
import App from './App.vue'
import router from './router'

import { Settings, type WeekSettings } from 'luxon'

import { registerSW } from 'virtual:pwa-register'

import VueDOMPurifyHTML from 'vue-dompurify-html'

import DOMPurify from 'dompurify'
import { useCommonStore } from '@/stores/CommonStore'
import { DEFAULT_FEATURE_FLAGS } from '@/config/defaultFeatureFlags'

//create app
const app = createApp(App)
if (import.meta.env.VITE_NODE_ENV) {
  app.config.performance = true
}

import i18n from './i18n'
app.use(i18n)

// luxon
Settings.throwOnInvalid = true
Settings.defaultWeekSettings = { firstDay: 7, minimalDays: 1, weekend: [6, 7] } // todo: localize

// TODO: Raygun Add Route tracking in router and setUser
//https://raygun.com/documentation/language-guides/javascript/vuejs/

app.config.errorHandler = function (err, vm, info) {
  rg4js('apiKey', import.meta.env.VITE_APP_RAYGUN_KEY)
  rg4js('enableCrashReporting', true)
  rg4js('saveIfOffline', true)
  rg4js('trackEvent', {
    type: 'pageView',
    path: '/' + window.location.pathname
  })
  // Raygun filtering will be handled in the error logic below
  rg4js('whitelistCrossOriginDomains', ['webpack-internal', '[native code]'])

  // Filter out Application Insights errors before sending to Raygun
  const errorMessage = (err as Error)?.message || err?.toString() || ''
  const shouldIgnore = [
    'AI (Internal)',
    'Failed to monitor XMLHttpRequest',
    'Failed to calculate the duration',
    'readystatechange',
    'monitoring data for this ajax call'
  ].some((pattern) => errorMessage.includes(pattern))

  if (!shouldIgnore) {
    rg4js('send', { error: err, customData: [{ info: info }] })
  }
}

//TODO: New way to check if element is in view in DOM
// vue2 app uses vue-check-view

import { ObserveVisibility } from 'vue-observe-visibility'

app.directive('observe-visibility', {
  beforeMount: (el, binding, vnode) => {
    ;(vnode as any).context = binding.instance
    ObserveVisibility.bind(el, binding, vnode)
  },
  updated: ObserveVisibility.update,
  unmounted: ObserveVisibility.unbind
})

import trackerPlugin from './plugins/trackerPlugin'
app.use(trackerPlugin)

// Form validation global validator
import { defineRule } from 'vee-validate'
import emailHelper from '@/exports/emailHelper'
import pwHelper from '@/exports/passwordHelper'
defineRule('required', (value: any) => {
  if (!value || !value.length) {
    return 'This field is required.'
  }
  return true
})
defineRule('email', (value: any) => {
  // Field is empty, should pass
  if (!value || !value.length) {
    return true
  }
  // Check if email
  if (value.match(emailHelper.getEmailRegex()) === null) {
    return 'Please provide a valid email address.'
  }
  return true
})
defineRule('password', (value: any) => {
  // Field is empty, should pass
  if (!value || !value.length) {
    return true
  }
  // Check if passwoed rules match
  if (value.match(pwHelper.getPasswordRegexRequirement()) === null) {
    return 'Please provide a password with at least 10 characters, one letter, one number, and one special character.'
  }
  return true
})
defineRule('phone', (value: any) => {
  if (!value || !value.length) {
    return true
  }
  if (value.match(phoneHelper.getPhoneRegex()) === null) {
    return 'This phone number is invalid.'
  }
  return true
})

import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
// eslint-disable-next-line vue/multi-word-component-names
app.component('Datepicker', Datepicker)

import phoneHelper from './exports/phoneHelper'

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getUrlMatchRegex() {
  //return /(([a-z]+:\/\/)?(([a-z0-9\-]+\.)+([a-z]{2}|aero|arpa|biz|com|coop|edu|gov|info|int|jobs|mil|museum|name|nato|net|org|pro|travel|local|internal|link))(:[0-9]{1,5})?(\/[a-z0-9_\-\.~]+)*(\/([a-z0-9_\-\.]*)(\?[a-z0-9+_\-\.%=&amp;]*)?)?(#[a-zA-Z0-9!$&'()*+.=-_~:@/?]*)?)(\s+|$)/gi
  return /(([a-z]+:\/\/)?(([a-z0-9\-]+\.)+(aero|arpa|biz|com|coop|edu|gov|info|int|jobs|mil|museum|name|nato|net|org|pro|travel|local|internal|link|[a-z]{2}))(:[0-9]{1,5})?(\/[a-z0-9_\-\.~]+)*(\/([a-z0-9_\-\.]*)(\?[a-z0-9+_\-\.%=&amp;]*)?)?(#[a-zA-Z0-9!$&'()*+.=-_~:@\?]*)?)([a-zA-Z0-9!$&'()*+.=-_~:@\?\#]+)/gi
}

app.directive('inline', (element) => {
  element.replaceWith(...element.children)
})

app.directive('highlight-text', (el, binding) => {
  const searchTerm = binding.value.searchTerm as string | string[]
  const _class = binding.value.class
  const _style = binding.value.style
  const loading = binding.value.loading
  let text = el.innerHTML as string

  if (loading) {
    return
  }

  // 1. sanitize text
  text = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: ['em', 'a'],
    ALLOWED_ATTR: ['href', 'target']
  })

  if (searchTerm && text) {
    let regex = null as RegExp | null

    if (Array.isArray(searchTerm) && searchTerm.length) {
      let regexString = escapeRegExp(searchTerm[0])

      for (const term of searchTerm.slice(1)) {
        regexString = `${regexString}|${escapeRegExp(term)}` // 2. for each search term, add to regex
      }

      regex = new RegExp(regexString, 'gi')
    } else if (!Array.isArray(searchTerm) && searchTerm.length) {
      regex = new RegExp(escapeRegExp(searchTerm), 'gi') // 2. add singlular term to regex
    }

    const replacement = `<span class="${_class}" style="${_style}">$&</span>`

    el.innerHTML = regex ? text.replace(regex, replacement) : text // 3. replace search term match with highlight style
  } else if (!searchTerm && text) {
    el.innerHTML = text
  }
})

app.directive('skeleton', (el, binding) => {
  const value = binding.value

  // If value is not an object or boolean, treat it as "not loading"
  if (value === undefined || value === null) {
    el.classList.remove('skeleton')
    return
  }

  const isBoolean = typeof value === 'boolean'
  const isObj = typeof value === 'object'

  const loading = isBoolean
    ? value
    : isObj && 'loading' in value && !!value.loading

  const minWidth = isObj && 'minWidth' in value ? Number(value.minWidth) : 4.5

  const maxWidth = isObj && 'maxWidth' in value ? Number(value.maxWidth) : 10

  if (loading) {
    el.classList.add('skeleton')

    if (!el.style.getPropertyValue('--skeleton-width').length) {
      const finalWidth = Math.random() * (maxWidth - minWidth) + minWidth
      el.style.setProperty('--skeleton-width', `${finalWidth}rem`)
    }
  } else {
    el.classList.remove('skeleton')
  }
})

app.directive('hyperlink', (el: HTMLElement) => {
  if (!el) {
    return
  }

  //https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript
  var replacedText = el.innerText.replace(getUrlMatchRegex(), (url: string) => {
    return url.includes('http')
      ? '<a href="' + url + '" target="_blank">' + url + '</a>'
      : '<a href="http://' + url + '" target="_blank">' + url + '</a>'
  })

  el.innerHTML = replacedText
})

// Scroll Position Directive
import { useScrollPosition } from './composables/useScrollPosition'

app.directive('scrollPosition', {
  mounted(el: HTMLElement, binding: DirectiveBinding<string>) {
    const id = binding.value

    const { scrollX, scrollY } = useScrollPosition(id)

    el.scrollLeft = scrollX.value ?? 0
    el.scrollTop = scrollY.value ?? 0
  },
  beforeUnmount(el: HTMLElement, binding: DirectiveBinding<string>) {
    const id = binding.value

    const { scrollX, scrollY } = useScrollPosition(id)

    scrollX.value = el.scrollLeft
    scrollY.value = el.scrollTop
  }
})

/* import the fontawesome core */
import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core'

/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

/* import specific icons */
import { all } from '@talkingparents/kit-4ad9ec01d5/icons'

/* add icons to the library */
library.add(...(all as IconDefinition[]))

// body scroll lock
import V3ScrollLock from 'v3-scroll-lock'
app.use(V3ScrollLock, {})

import { PiniaColada } from '@pinia/colada'

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

app.use(pinia)
app.use(PiniaColada, {
  queryOptions: {
    staleTime:
      import.meta.env?.MODE === 'production' ? 5 * 60 * 1000 : 1 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  }
})
app.use(router)

app.component('FontAwesomeIcon', FontAwesomeIcon)

//----------------------------------Begin AI----------------------------------
// code in vue-appinsights.js is copied from https://github.com/latelierco/vue-application-insights and modified
import VueAppInsights from './vue-appinsights'
const connection: string =
  import.meta.env.VITE_APP_APP_INSIGHTS_CONNECTION_STRING ||
  'InstrumentationKey=44268c94-5787-4788-a69e-659c4e81122e;IngestionEndpoint=https://northcentralus-0.in.applicationinsights.azure.com/;LiveEndpoint=https://northcentralus.livediagnostics.monitor.azure.com/'

app.use(VueAppInsights, { connectionString: connection, router })
//----------------------------------End AI----------------------------------

import loading from '@/components/library/Loading.vue'
// eslint-disable-next-line vue/multi-word-component-names
app.component('loading', loading)

//dom purify
app.use(VueDOMPurifyHTML, { default: { ALLOWED_TAGS: ['em'] } })

import Vue3Toastify, { toast, type ToastContainerOptions } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

app.use(Vue3Toastify, {
  autoClose: 3000, //TODO figure out good config defaults
  closeButton: false,
  position: toast.POSITION.TOP_CENTER,
  icon: false,
  transition: { enter: 'toast-enter', exit: 'toast-exit', collapse: false },
  clearOnUrlChange: false,
  useHandler: (toastApp: ReturnType<(typeof import('vue'))['createApp']>) => {
    toastApp.use(i18n)
    toastApp.component('FontAwesomeIcon', FontAwesomeIcon)
  }
} as ToastContainerOptions)

// Rewardful
import RewardfulPlugin from '@/plugins/rewardfulPlugin'
app.use(RewardfulPlugin, { key: import.meta.env.VITE_APP_REWARDFUL_API_KEY })

// Initialize service worker
import { setupServiceWorker } from '@/services/serviceWorkerService'
setupServiceWorker()

const commonStore = useCommonStore()
// Set defaults immediately for consistent startup behavior
commonStore.setFeatureFlags(DEFAULT_FEATURE_FLAGS)

import { vMaska } from 'maska/vue'
app.directive('maska', vMaska)

commonStore.loadRemoteConfigFlags().then(() => {
  app.mount('#app')

  // After mount, check if there was a pending route due to SW update
  const pendingRoute = sessionStorage.getItem('pendingRoute')
  if (pendingRoute) {
    router.replace(pendingRoute)
    sessionStorage.removeItem('pendingRoute')
  }
})
